import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Progress } from "antd";
import DockDoorStatus from "./DockDoorStatus";
import PalletsInTrailer from "./PalletsInTrailer";
import DockDoorTopView from "./DockDoorTopView";
import useModalClose from "../../../hooks/useModalClose";
import {
  closeParticularMinimizedModal,
  getTimeBasedOnUserTime
} from "../../../util/helper/helperFunctions";
import { MINIMIZE_COUNT } from "../../../util/helper/constants";
import { AppContext } from "../../../Context/Context";
import minimizeButton from "../../../assets/icons/minimize.png";
import closeButton from "../../../assets/icons/close.png";
import "../../../assets/css/DockDoor/dockDoorModal.css";
import { useLocation } from "react-router-dom";
import useMinimizedModalFeature from "../../../hooks/useMinimizedModalFeature";

const NO_DATA = "No Data";

const checkIfFalsy = (data) => (data === 0 ? false : !data);

const groupDockDoorWithPallets = (data) =>
  Object.groupBy(data, ({ position }) => position);

const MODAL_ID = "dock-door-wrapper-id";

const DockDoorModal = ({ dockData }) => {
  const {
    isMarkerPopupOpen,
    setIsMarkerPopupOpen,
    minimizedWebObjectsList,
    setMinimizedWebObjectsList,
    setAlertObject,
  } = useContext(AppContext);

  const { timeMachineV2ModeIsEnabled } = useSelector(
    (state) => state.newTimeMachine
  );

  const { feature } = useMinimizedModalFeature();

  const { pathname } = useLocation();

  const [loading, setLoading] = useState(true);
  const [dockDoorDetails, setDockDoorDetails] = useState({});
  const [palletsInDockDoor, setPalletsInDockDoor] = useState({});
  const [dockDoorStatus, setDockDoorStatus] = useState({});
  const [palletsInTrailer, setPalletsInTrailer] = useState([]);
  const [minimizeInitiated, setMinimizeInitiated] = useState(false);

  const modalCloseHandler = () => {
    document.body.classList.remove("prevent-scroll");
    setIsMarkerPopupOpen(false);
  };

  const handleDockDoorTopViewData = (data) => {
    const groupedDocks = groupDockDoorWithPallets(data);
    setPalletsInDockDoor(groupedDocks);
  };

  const { data: dockDoorsData } = useSelector((state) => state.plant.dockDoors);

  const newTimeMachineDockDoorsData = useSelector(
    (state) => state.newTimeMachine.dockDoorsData
  );

  const isNewTimeMachineActive = useMemo(() => {
    return pathname.includes("new-time-machine") && timeMachineV2ModeIsEnabled;
  }, [pathname, timeMachineV2ModeIsEnabled]);

  const getFlattenedValue = (data) => Object.values(data).flat(Infinity);

  useEffect(() => {
    if (isMarkerPopupOpen) {
      document.body.classList.add("prevent-scroll");

      const dockDoorData = isNewTimeMachineActive
        ? getFlattenedValue(newTimeMachineDockDoorsData)
        : getFlattenedValue(dockDoorsData);

      if (dockDoorData.length > 0) {
        const dockDetailsArray = dockDoorData.filter(
          ({ dock }) => dock === dockData[0].dock
        );
        if (dockDetailsArray.length > 0) {
          const dockDoorStatus = {
            lgvInTrailer: dockDetailsArray[0].lgvInTrailer,
            doorOpen: dockDetailsArray[0].doorOpen,
            trailerAtDoor: dockDetailsArray[0].trailerAtDoor,
            readyForLoading: dockDetailsArray[0].readyForLoading,
            doorInManual: dockDetailsArray[0].doorInManual,
            airbagHold: dockDetailsArray[0].airbagHold,
            fault: dockDetailsArray[0].fault
          };

          setDockDoorStatus(dockDoorStatus);
          setPalletsInTrailer(dockDetailsArray);
          // handle data for top view through socket and update the state
          handleDockDoorTopViewData(dockDetailsArray);

          const allocated = dockDetailsArray.filter(
            ({ palletStatus }) => palletStatus === "Allocated"
          ).length;

          const released = dockDetailsArray.filter(
            ({ palletStatus }) => palletStatus === "Released"
          ).length;

          const active = dockDetailsArray.filter(
            ({ palletStatus }) =>
              ![
                "Allocated",
                "Released",
                "Completed",
                "Canceled",
                "Aborted"
              ].includes(palletStatus)
          ).length;

          const remaining = allocated + released + active;
          const completed = dockDetailsArray.filter(
            ({ palletStatus }) => palletStatus === "Completed"
          ).length;
          const progress = Math.trunc(
            (completed * 100) / dockDetailsArray.length
          );

          const dockSummary = {
            ...dockDetailsArray[0],
            allocated,
            released,
            active,
            remaining,
            progress
          };
          setDockDoorDetails(dockSummary);
        }
        setLoading(false);
      }
    }
  }, [
    isMarkerPopupOpen,
    dockDoorsData,
    dockData,
    newTimeMachineDockDoorsData,
    isNewTimeMachineActive
  ]);

  const minimizeModalHandler = (e) => {
    e.stopPropagation();
    const currentDockList = [...minimizedWebObjectsList[feature].docks];

    const isDockAlreadyMinimized = currentDockList.some(
      (item) => item[0]?.dock === dockData[0]?.dock
    );

    if (
      currentDockList.length === MINIMIZE_COUNT.docks &&
      !isDockAlreadyMinimized
    ) {
      setAlertObject({
        type: "warning",
        payload: {
          heading: "Limit reached!",
          description: "Cannot minimize more than 10 Dock Doors..."
        }
      });
      return;
    }

    if (!isDockAlreadyMinimized) {
      currentDockList.push(dockData);
      setMinimizedWebObjectsList((prev) => ({
        ...prev,
        [feature]: {
          ...prev[feature],
          docks: currentDockList
        }
      }));

      setMinimizeInitiated(true);
    }

    // Always close modal (intentional user click)
    setTimeout(() => {
      modalCloseHandler();
    }, 450);
  };

  const renderDockDoorDetails = () => {
    if (loading) {
      return (
        <div className="dock-door-brief-loader">
          <h4>Fetching Details...</h4>
          <div className="spinner"></div>
        </div>
      );
    } else if (Object.keys(dockDoorDetails).length > 0) {
      return (
        <>
          <div className="detail-section-container">
            {[
              {
                label: "Shipment",
                value: dockDoorDetails?.shipmentId
              },
              {
                label: "Last Updated",
                value: getTimeBasedOnUserTime(
                  dockDoorDetails?.currentDateTime
                ).format("M/D/YYYY h:mm:ss A z")
              }
            ].map(({ label, value }) => (
              <div key={label} className="detail-section">
                <h4>{label}</h4>
                <p>{!checkIfFalsy(value) ? value : NO_DATA}</p>
              </div>
            ))}
          </div>
          <div className="detail-section-container column-section">
            <div className="additional-data-section-title">
              <h3>Shipment Status</h3>
            </div>
            <div className="detail-section-container">
              {[
                {
                  label: "Load Time",
                  value: dockDoorDetails?.loadTime
                },
                {
                  label: "Process Time",
                  value: dockDoorDetails?.processTime
                },
                { label: "Type", value: dockDoorDetails?.type },
                { label: "Status", value: dockDoorDetails?.dockStatus },
                {
                  label: "Allocated Pallets",
                  value: dockDoorDetails?.allocated
                },
                {
                  label: "Remaining Pallets",
                  value: dockDoorDetails?.remaining
                },
                {
                  label: "Released Pallets",
                  value: dockDoorDetails?.released
                },
                {
                  label: "Active Pallets",
                  value: dockDoorDetails?.active
                },
                {
                  label: "Progress",
                  value: dockDoorDetails?.progress
                }
              ].map(({ label, value }) => (
                <div key={label} className="detail-section">
                  <h4>{label}</h4>
                  {label !== "Progress" ? (
                    <p>{!checkIfFalsy(value) ? value : NO_DATA}</p>
                  ) : (
                    <Progress percent={!checkIfFalsy(value) ? value : 0} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }
  };

  const closeMinimizedDockDoorModal = () => {
    closeParticularMinimizedModal({
      list: minimizedWebObjectsList[feature].docks,
      setMinimizedWebObjectsList,
      item: dockData,
      type: "docks",
      feature
    });
  };

  useModalClose(MODAL_ID, () => {
    modalCloseHandler();
    closeMinimizedDockDoorModal();
  });

  return (
    <div id={MODAL_ID} className="dock-door-wrapper">
      <div
        className={`dock-door-wrapper-container ${
          minimizeInitiated ? "modal-animation" : ""
        }`}
      >
        <div className="dock-door-details-section">
          <div className="dock-door-details-header">
            <div className="left-detail-title-section">
              <h3>
                Door{" "}
                {dockDoorDetails?.dock
                  ? Number(dockDoorDetails?.dock?.split(".")[1])
                  : "Loading..."}
              </h3>
              <div className="modal-action-buttons">
                <div
                  onClick={minimizeModalHandler}
                  className="modal-action-button-wrapper"
                >
                  <img
                    className="modal-action-button-no-height"
                    src={minimizeButton}
                    alt="minimize-button"
                  />
                </div>
                <div
                  onClick={() => {
                    modalCloseHandler();
                    closeMinimizedDockDoorModal();
                  }}
                  className="modal-action-button-wrapper"
                >
                  <img
                    className="modal-action-button"
                    src={closeButton}
                    alt="close-button"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="dock-door-details-body">
            <div className="dock-door-detail-left-section">
              {dockDoorDetails?.shipmentId && renderDockDoorDetails()}
              <DockDoorStatus
                loading={loading}
                dockDoorStatus={dockDoorStatus}
              />
              {dockDoorDetails?.shipmentId && (
                <PalletsInTrailer
                  loading={loading}
                  palletsInTrailer={palletsInTrailer}
                />
              )}
            </div>
            <div className="dock-door-detail-right-section">
              {palletsInDockDoor && (
                <DockDoorTopView
                  loading={loading}
                  palletsInDockDoor={palletsInDockDoor}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DockDoorModal;
